import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Globe, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const TranslationManagement = () => {
  const [loading, setLoading] = useState(false);
  const [forceRetranslate, setForceRetranslate] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const [instructionsMessage, setInstructionsMessage] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  // Charger les instructions personnalisées au montage
  React.useEffect(() => {
    loadCustomInstructions();
  }, []);

  const loadCustomInstructions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/settings/translation-instructions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomInstructions(data.instructions || '');
      }
    } catch (err) {
      console.error('Erreur chargement instructions:', err);
    }
  };

  const handleSaveInstructions = async () => {
    setInstructionsLoading(true);
    setInstructionsMessage(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/settings/translation-instructions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ instructions: customInstructions })
      });

      if (response.ok) {
        setInstructionsMessage({ type: 'success', text: '✅ Instructions sauvegardées avec succès' });
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde instructions:', err);
      setInstructionsMessage({ type: 'error', text: '❌ Erreur lors de la sauvegarde' });
    } finally {
      setInstructionsLoading(false);
    }
  };

  const handlePreloadTranslations = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError("Vous n'êtes pas authentifié. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }
      
      const response = await fetch(
        `${API_BASE}/api/admin/translations/preload-all?force_retranslate=${forceRetranslate}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setResult(data);
      
    } catch (err) {
      console.error('Erreur préchargement traductions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir vider TOUT le cache de traduction ? Cette action est irréversible.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError("Vous n'êtes pas authentifié. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }
      
      const response = await fetch(
        `${API_BASE}/api/admin/translations/cache`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      alert(data.message || 'Cache vidé avec succès');
      setResult(null);
      
    } catch (err) {
      console.error('Erreur vidage cache:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" style={{ fontFamily: '"Lora", serif' }}>
      <div className="mb-8">
        <h1 
          className="text-4xl font-bold mb-2"
          style={{ color: 'rgb(196, 150, 96)' }}
        >
          Gestion des Traductions
        </h1>
        <p className="text-gray-600 text-lg">
          Traduisez automatiquement tout le site dans les 5 langues (EN, NL, DE, ES, IT)
        </p>
      </div>

      {/* Card instructions personnalisées */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle 
            className="flex items-center gap-2 text-2xl"
            style={{ color: 'rgb(196, 150, 96)' }}
          >
            📝 Instructions Personnalisées de Traduction
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-900 text-sm">
              <strong>💡 Utilisez ce champ pour corriger des traductions spécifiques.</strong>
              <br />
              Par exemple : "Si tu traduis 'LES LIGNAGES DE BRUXELLES' en néerlandais, la traduction doit être : De Brusselse Geslachten."
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Instructions pour le traducteur IA (toutes langues) :
            </label>
            <textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Exemple : Si tu traduis 'LES LIGNAGES DE BRUXELLES' en néerlandais, la traduction doit être : De Brusselse Geslachten."
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              style={{ fontFamily: '"Lora", serif' }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Ces instructions seront ajoutées au prompt de traduction pour toutes les langues.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={handleSaveInstructions}
              disabled={instructionsLoading}
              style={{ 
                backgroundColor: instructionsLoading ? '#9ca3af' : 'rgb(196, 150, 96)',
                color: 'white'
              }}
            >
              {instructionsLoading ? 'Sauvegarde...' : 'Sauvegarder les instructions'}
            </Button>

            {instructionsMessage && (
              <span className={instructionsMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}>
                {instructionsMessage.text}
              </span>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Important :</strong> Après avoir modifié ces instructions, cochez "Forcer la retraduction" ci-dessous pour que les changements prennent effet sur les textes déjà traduits.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Card principale */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle 
            className="flex items-center gap-2 text-2xl"
            style={{ color: 'rgb(196, 150, 96)' }}
          >
            <Globe className="w-6 h-6" />
            Pré-traduction Automatique
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Description */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">📝 Ce qui sera traduit :</h3>
            <ul className="list-disc list-inside text-blue-800 space-y-1">
              <li>Toutes les news (titres et contenus)</li>
              <li>Tous les événements (titres, descriptions, lieux)</li>
              <li>Toutes les pages du site (titres et contenus)</li>
            </ul>
            <p className="text-sm text-blue-700 mt-3">
              ⏱️ Durée estimée : 2-5 minutes selon la quantité de contenu
            </p>
          </div>

          {/* Option force retranslate */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="forceRetranslate"
              checked={forceRetranslate}
              onChange={(e) => setForceRetranslate(e.target.checked)}
              className="w-5 h-5 cursor-pointer"
            />
            <label htmlFor="forceRetranslate" className="cursor-pointer">
              <span className="font-semibold">Forcer la retraduction</span>
              <p className="text-sm text-gray-600">
                Retraduit TOUT, même ce qui est déjà en cache (plus coûteux)
              </p>
            </label>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-4">
            <Button
              onClick={handlePreloadTranslations}
              disabled={loading}
              className="flex-1 py-6 text-lg font-semibold"
              style={{ 
                backgroundColor: loading ? '#9ca3af' : 'rgb(196, 150, 96)',
                color: 'white'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Traduction en cours...
                </>
              ) : (
                <>
                  <Globe className="w-5 h-5 mr-2" />
                  Traduire tout le site
                </>
              )}
            </Button>

            <Button
              onClick={handleClearCache}
              disabled={loading}
              variant="outline"
              className="py-6 text-lg"
              style={{ 
                borderColor: 'rgb(196, 150, 96)',
                color: 'rgb(196, 150, 96)'
              }}
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Vider le cache
            </Button>
          </div>

          {/* Message de succès */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-semibold text-green-900">
                  {result.status === 'started' ? 'Traduction lancée !' : 'Traduction terminée avec succès !'}
                </h3>
              </div>

              {/* Message pour traduction en arrière-plan */}
              {result.status === 'started' && (
                <div className="bg-white rounded-lg p-4 mb-4">
                  <p className="text-gray-800 mb-2">
                    <strong>{result.message}</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    {result.info}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-blue-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Traduction en cours... Le site reste accessible pendant ce temps.</span>
                  </div>
                </div>
              )}

              {/* Statistiques globales - uniquement si disponibles */}
              {result.statistics && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {result.statistics.total_texts}
                      </div>
                      <div className="text-sm text-gray-600">Textes traités</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {result.statistics.translated}
                      </div>
                      <div className="text-sm text-gray-600">Nouvelles traductions</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-purple-600">
                        {result.statistics.cached}
                      </div>
                      <div className="text-sm text-gray-600">Déjà en cache</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-red-600">
                        {result.statistics.errors}
                      </div>
                      <div className="text-sm text-gray-600">Erreurs</div>
                    </div>
                  </div>

                  {/* Statistiques par collection */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-green-900">Détails par type :</h4>
                    {result.statistics.by_collection.news && (
                      <div className="flex justify-between bg-white rounded p-3">
                        <span className="font-medium">📰 News</span>
                        <span className="text-gray-600">{result.statistics.by_collection.news.total} textes</span>
                      </div>
                    )}
                    {result.statistics.by_collection.events && (
                      <div className="flex justify-between bg-white rounded p-3">
                        <span className="font-medium">📅 Événements</span>
                        <span className="text-gray-600">{result.statistics.by_collection.events.total} textes</span>
                      </div>
                    )}
                    {result.statistics.by_collection.pages && (
                      <div className="flex justify-between bg-white rounded p-3">
                        <span className="font-medium">📄 Pages</span>
                        <span className="text-gray-600">{result.statistics.by_collection.pages.total} textes</span>
                      </div>
                    )}
                  </div>

                  {/* Estimation coûts */}
                  <div className="mt-4 p-3 bg-white rounded">
                    <p className="text-sm text-gray-700">
                      💰 <span className="font-semibold">Coût estimé :</span> ~
                      {(result.statistics.translated * 0.0001).toFixed(4)}€ 
                      <span className="text-gray-500 ml-1">
                        (GPT-4o-mini)
                      </span>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <h3 className="font-semibold text-red-900">Erreur</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card informations */}
      <Card>
        <CardHeader>
          <CardTitle style={{ color: 'rgb(196, 150, 96)' }}>
            ℹ️ Informations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Quand utiliser cette fonction ?</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li>Après avoir ajouté plusieurs news ou événements</li>
              <li>Après la mise à jour du contenu des pages</li>
              <li>Avant un événement important pour garantir que tout est traduit</li>
              <li>Une fois par mois pour rafraîchir les traductions (avec "Forcer la retraduction")</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Langues supportées :</h3>
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded">🇬🇧 Anglais</span>
              <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded">🇳🇱 Néerlandais</span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded">🇩🇪 Allemand</span>
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded">🇪🇸 Espagnol</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded">🇮🇹 Italien</span>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">💡 Astuce :</span> Le cache intelligent évite de retraduire ce qui est déjà fait. 
              Vous pouvez lancer cette fonction régulièrement sans surcoût !
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TranslationManagement;
